import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { Select } from 'antd';

import { useOptionList } from '@src/hooks/useOptionLists';

import { filterSelectOption } from './selectFilterOption';

interface ListSelectValue {
  value: string;
  label: string;
}

interface ListSelectInputProps {
  organizationId: string;
  divisionId?: string;
  optionListId?: string;
  placeholder?: string;
  value?: ListSelectValue;
  onChange?: (value: ListSelectValue | undefined) => void;
}

/**
 * Selector for a `listSelect` field. Items come from the division mirror of the option list
 * (readable by that division's users) and are searched locally; the value keeps the item id and
 * its label so the answer survives later renames.
 */
const ListSelectInput: React.FC<ListSelectInputProps> = ({
  organizationId,
  divisionId,
  optionListId,
  placeholder,
  value,
  onChange,
}) => {
  const intl = useIntl();
  const { list, loading, error } = useOptionList(organizationId, optionListId, divisionId);

  const options = useMemo(
    () => (list?.items ?? []).filter(item => item.active).map(item => ({ value: item.id, label: item.label })),
    [list],
  );

  return (
    <Select
      allowClear
      labelInValue
      loading={loading}
      // A missing division copy or a denied read must not look like an empty list.
      notFoundContent={intl.formatMessage({
        id: error ? 'dynamic.form.listSelect.loadError' : 'dynamic.form.listSelect.empty',
      })}
      status={error ? 'error' : undefined}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      showSearch
      filterOption={filterSelectOption}
      value={value}
      virtual
    />
  );
};

export default ListSelectInput;
